import React, { Component } from 'react';
import PropTypes from 'prop-types';
import 'isomorphic-fetch';
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import Context from '../../contexts/MarketPerformanceContext';
import InstrumentGroups from './InstrumentGroups';
import Store from '../../utilities/Store';
import { setupAnalytics } from '../../../primary/components/analytics/default';
import { marketsView } from '../../../primary/components/analytics/events/markets';

const analytics = setupAnalytics();

class InstrumentPerformance extends Component {
    state = {
        currentView: 'default', // A temporary view switching solution
        currentTabId: 0,
        currentIndexId: '',
        currentChartRangeId: '1Y',
        loading: true,
        showError: false,
        header: {
            text: 'Market performance',
        },
        tabs: [
            {
                id: 0,
                name: 'Loading',
            },
        ],
        marketIndex: {
            indices: [
                {
                    instrumentID: '',
                    name: 'Loading',
                },
            ],
            otherIndicesExist: true,
        },
        chartRanges: [],
        fullIndices: [],
        fullInstruments: [],
        error: {
            code: 0,
            message: 'Market data is unavailable',
        },
    };

    componentDidMount = () => {
        const { initialState } = this.props;

        // Set initial state
        this.setState(
            JSON.parse(atob(initialState)),
            this.fetchMarketData,
        );
    };

    componentWillUnmount() {
        console.log('Unmounting Market Performance component');
        this.controller.abort();
    }

    afterFetch = () => {
        if (this.state.currentTabId === 'asx') {
            analytics.trackEvent(marketsView({ title: 'View ASX' }));
        }

        if (this.state.currentTabId === 'nzx') {
            analytics.trackEvent(marketsView({ title: 'View NZX' }));
        }

        // A temporary solution for switching view
        if (this.state.currentTabId === 'nzdx') {
            this.setState({ currentView: 'InstrumentDetails' });
            analytics.trackEvent(marketsView({ title: 'View NZ DX' }));

            return;
        }

        // Fallback to first index (used for ASX and NZX)
        if (!this.state.currentIndexId
            && this.state.marketIndex
            && this.state.marketIndex.indices
            && this.state.marketIndex.indices.length
        ) {
            this.setState({ currentIndexId: this.state.marketIndex.indices[0].instrumentId });
        }
    };

    getStore = () => Store;

    setCurrentTab = (id) => {
        const tab = Store.getEntry(`preserveState${id}`);

        this.setState(
            {
                loading: true,
                currentTabId: id,
                currentIndexId: tab ? tab.currentIndexId : null,
                currentView: 'default',
            },
            this.fetchMarketData,
        );
    };

    setCurrentIndex = (id) => {
        Store.setEntry(
            `preserveState${this.state.currentTabId}`,
            {
                currentIndexId: id,
            },
        );

        this.setState(
            {
                currentIndexId: id,
                instrument: {
                    loading: true,
                },
            },
            this.fetchMarketData,
        );
    };

    setCurrentChartRange = (id) => {
        if (this.state.currentChartRangeId === id) {
            return;
        }

        this.setState(
            {
                currentChartRangeId: id,
                instrument: { loading: true },
            },
            this.fetchMarketData,
        );
    };

    getDataParams = () => {
        const { currentChartRangeId, currentIndexId } = this.state;

        return {
            currentChartRangeId,
            currentIndexId,
        };
    };

    fetchMarketData = () => {
        const urlParams = Object.entries(this.getDataParams()).map(([key, val]) => `${key}=${val}`).join('&');

        // The back-end react handler expects us to call `/state`
        return this.fetchData(
            `${window.location.href.replace(/\/$/, '')}/state?${urlParams}`,
        );
    };

    fetchData = (url) => {
        if (this.controller) {
            // Abort previous fetch
            this.controller.abort();
        }

        // Re-instantiate the abort controller
        this.controller = new AbortController();

        // Hoist
        const { signal } = this.controller;

        return fetch(url, {
            method: 'get',
            signal,
            headers: {
                'x-requested-with': 'XMLHttpRequest',
            },
        })
            .then((response) => response.json())
            .then((data) => {
                this.setState({ loading: false, ...data });
                this.afterFetch();
            })
            .catch((error) => {
                if (error instanceof DOMException) {
                    if (error.name !== 'AbortError') {
                        console.log(error.code, error.message, error.name);
                    } else {
                        // Do nothing for aborted XHR
                        return;
                    }
                } else {
                    console.log(error);
                }

                this.setState({ loading: false, showError: true });
            });
    };

    fetchFullIndices = () => {
        const getParams = {
            currentTabId: this.state.currentTabId,
        };
        const url = `${window.location.href.replace(/\/$/, '')}/state/handlefullindicesrequest?${Object.entries(getParams).map(([key, val]) => `${key}=${val}`).join('&')}`;
        return this.fetchData(url);
    };

    getFullIndices = () => {
        this.setState(
            {
                loading: true,
                currentView: 'FullIndices', // A temporary view switching solution
            },
            this.fetchFullIndices,
        );
    };

    backToOverview = () => {
        this.setState(
            {
                showError: false,
                currentView: 'default', // A temporary view switching solution
            },
            this.fetchMarketData,
        );
    };

    render = () => <Context.Provider value={{
        state: this.state,
        setCurrentTab: this.setCurrentTab,
        setCurrentIndex: this.setCurrentIndex,
        setCurrentChartRange: this.setCurrentChartRange,
        getFullIndices: this.getFullIndices,
        backToOverview: this.backToOverview,
    }}>
        <InstrumentGroups />
    </Context.Provider>
}

InstrumentPerformance.propTypes = {
    initialState: PropTypes.string,
};

export default InstrumentPerformance;
